import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HaevnLogo } from "../components/Navigation";
import SEO from "../components/SEO";
import { trackSurveyStart, track } from "../lib/track";

/**
 * /whynot — a cinematic full-bleed horizontal slider.
 *
 * Each "chapter" of the HAEVN pitch lives on its own viewport-filling slide
 * with a city photograph behind it. A dark overlay carries the editorial
 * serif copy on the left so it reads cleanly over the imagery.
 *
 * Behavior:
 *   - Fully user-driven (no autoplay) — the reader controls the pace.
 *   - Manual prev/next + bottom progress dots.
 *   - Keyboard ←/→ navigation when the track is focused.
 *   - Touch swipe + finger-follow drag on mobile.
 *   - SEO + per-route metadata.
 */

// Local optimized image assets. Originals on the customer-assets CDN
// were 1.3-1.8 MB on disk but up to 4672×5840 pixels — which decodes to
// ~100 MB in RAM per image and silently fails to render on iOS Safari /
// low-memory Android devices once total decoded image memory exceeds the
// platform ceiling (~150-225 MB). These resized JPEG copies cap at 1600px
// on the longest edge and total ~700 KB on disk / ~25 MB decoded across
// the 5 slides. Plenty sharp on mobile retina; no more silent-failure
// black slides on older devices.
const PHONE_USER = "/images/whynot/phone-user.jpg";
const INTRO_USER = "/images/whynot/intro-user.jpg";
const ROOFTOP_PARTY = "/images/whynot/rooftop-party.jpg";
const BAR_CROWD = "/images/whynot/bar-crowd.jpg";
const DINNER_LAUGH = "/images/whynot/dinner-laugh.jpg";

// Two overlay styles. The default "left" style is used for cinematic
// city establishing shots and creates a left → right dim-to-bright
// gradient that protects copy in the bottom-left. The "bottom" style
// is used for subject-led shots where the person needs to remain
// visible; the gradient runs bottom → top, darker only behind the
// copy.
const OVERLAYS = {
  left:
    "linear-gradient(to right, rgba(8, 11, 24, 0.78) 0%, rgba(8, 11, 24, 0.55) 40%, rgba(8, 11, 24, 0.20) 75%, rgba(8, 11, 24, 0.10) 100%)",
  bottom:
    "linear-gradient(to top, rgba(8, 11, 24, 0.90) 0%, rgba(8, 11, 24, 0.70) 25%, rgba(8, 11, 24, 0.35) 55%, rgba(8, 11, 24, 0.05) 80%, transparent 100%)",
};

// Stronger "raised" gradients applied to all chapter slides (1, 2, 3,
// 4) so the eyebrow and chapter number consistently sit on a solid
// base regardless of the warmth/cool tones in the underlying image.
// The intro/hero keeps the softer OVERLAYS.bottom defaults because
// its book-cover composition leans on more of the photograph showing.
export const RAISED_OVERLAY_DESKTOP =
  "linear-gradient(to top, rgba(8, 11, 24, 0.95) 0%, rgba(8, 11, 24, 0.85) 30%, rgba(8, 11, 24, 0.55) 55%, rgba(8, 11, 24, 0.20) 80%, transparent 100%)";
export const RAISED_OVERLAY_MOBILE =
  "linear-gradient(to top, rgba(0, 0, 0, 0.97) 0%, rgba(0, 0, 0, 0.85) 25%, rgba(0, 0, 0, 0.55) 45%, rgba(0, 0, 0, 0.20) 60%, transparent 75%)";

const SLIDES = [
  {
    id: 0,
    intro: true,
    image: INTRO_USER,
    // Subject (silhouetted figure) sits in the LEFT half of the source
    // image. Flip horizontally so the subject moves to the right and
    // the warm sunlight bathes the left where the copy lands.
    flip: true,
    overlay: "bottom",
    eyebrow: "Because you have better things to do...",
    title: "Your personal life shouldn't feel like a part-time job.",
    body: [
      "HAEVN sits right between a regular dating app and an expensive personal matchmaker. We took the best of both worlds to create a real dating service that actually works for regular people, stays affordable, and keeps things as human as possible.",
    ],
  },
  {
    id: 1,
    image: PHONE_USER,
    // Subject is on source-LEFT, so flip horizontally to move him to
    // visual-right and clear the bottom-left copy column.
    flip: true,
    overlay: "bottom",
    overlayDesktop: RAISED_OVERLAY_DESKTOP,
    overlayMobile: RAISED_OVERLAY_MOBILE,
    // This image is a face closeup — head sits in the TOP half of the
    // source. Use no mobile zoom (100% height) so the full source
    // height fits the viewport; this prevents the closeup from
    // feeling too tight on mobile while keeping the horizontal flip
    // for desktop composition.
    mobileImageTop: "0",
    mobileImageHeight: "100%",
    eyebrow: "What is this?",
    title:
      "It's not a dating app. It's a dating service.",
    body: [
      "Apps are built to keep you on your phone staring at screens. HAEVN is built to get you out to meet people in person. You tell us who you are and what you want, and we text you when we find matches.",
    ],
  },
  {
    id: 2,
    image: ROOFTOP_PARTY,
    // The new image is a group composition with the subject (woman with
    // curly hair, laughing) horizontally centered. No flip — leave the
    // image as-is and let `object-cover center` keep her in the center
    // of the mobile viewport.
    overlay: "bottom",
    overlayDesktop: RAISED_OVERLAY_DESKTOP,
    overlayMobile: RAISED_OVERLAY_MOBILE,
    // On mobile she sits in the lower-center of the source, which puts
    // her face right behind the title text. Use a vertical-only zoom
    // hack: 150% height with a -50% top offset pushes her face up into
    // the upper third of the screen, comfortably above the gradient
    // and copy zone.
    mobileImageTop: "-50%",
    mobileImageHeight: "150%",
    eyebrow: "How does it work?",
    title: "Your rules come first..",
    body: [
      "First, we use hard \"gates\" to instantly filter out your absolute dealbreakers, like age, distance, and intent. If those don't align, you'll never see each other. Then, instead of you swiping or using AI to guess who you'll like, we use statistical probability (math) to estimate compatibility. Every week, we send you the matches who pass your rules and share an 80% alignment or higher.",
    ],
    // Optional overview video — shown as a tiny inline text link
    // below the body copy on this slide only. Tap opens a lightbox.
    // No thumbnail, no length call-out — minimal disruption to the
    // cinematic composition of the slide.
    video: {
      src: "/videos/whynot-overview.mp4",
      label: "Watch the overview",
    },
  },
  {
    id: 3,
    image: BAR_CROWD,
    // Subject (blond man) sits in the right-center of the source. No
    // flip — he's already on the right where we want him visually.
    // On mobile portrait, bias the crop right (65%) so his face
    // stays in frame.
    overlay: "bottom",
    mobileObjectPosition: "65% center",
    // Slight zoom + small upward lift so the man's face sits higher
    // on mobile without filling the viewport. Tuned to keep his
    // forehead through chin visible.
    mobileImageHeight: "115%",
    mobileImageTop: "-12%",
    overlayDesktop: RAISED_OVERLAY_DESKTOP,
    overlayMobile: RAISED_OVERLAY_MOBILE,
    eyebrow: "Would this work for me?",
    title:
      "Built for people who are tired of wasting time.",
    body: [
      "Dating apps can feel like a part-time job. HAEVN takes 15 minutes, one time. It is the easiest way to keep yourself open to meeting the right person without the daily headache.",
    ],
  },
  {
    id: 4,
    image: DINNER_LAUGH,
    // Flip horizontally so the people group moves from the source-
    // right toward visual-right (and the silhouetted man's profile,
    // originally on source-left, becomes a dim zone on the
    // visual-left where the copy column lives). Same pattern used
    // by slides 1 and 2.
    flip: true,
    overlay: "bottom",
    // The dinner scene has bright wine glasses + warm lamps along
    // the lower edge that compete with the type. Use a slightly
    // stronger bottom gradient than the shared RAISED defaults to
    // pull the floor down so the type and CTA always read.
    overlayDesktop:
      "linear-gradient(to top, rgba(8, 11, 24, 0.97) 0%, rgba(8, 11, 24, 0.90) 30%, rgba(8, 11, 24, 0.65) 55%, rgba(8, 11, 24, 0.30) 80%, transparent 100%)",
    overlayMobile:
      "linear-gradient(to top, rgba(0, 0, 0, 0.98) 0%, rgba(0, 0, 0, 0.90) 25%, rgba(0, 0, 0, 0.65) 45%, rgba(0, 0, 0, 0.30) 60%, transparent 75%)",
    // After the flip, the woman ends up at roughly source-LEFT
    // (since she was source-right and mirroring inverted that).
    // On mobile we want her visible in the upper viewport, so bias
    // crop to source-LEFT — which after mirror becomes visual-RIGHT.
    mobileObjectPosition: "60% center",
    // Disable the default flipped-slide zoom (135%/-17.5%) — the
    // dinner shot doesn't need vertical re-positioning, just the
    // horizontal mirror. Force 100%/0 so object-position takes effect
    // on a viewport-sized image.
    mobileImageHeight: "100%",
    mobileImageTop: "0",
    // Closing chapter — hide the eyebrow + counter so the headline
    // carries the final moment. The CTA does the rest.
    hideCounter: true,
    title: "So what are you waiting for?",
    numberedList: [
      "There's no app to download.",
      "Only takes 15 minutes, one time.",
      "You never have to manage or update a profile.",
      "We send your matches straight to you once a week.",
      "It's free to join, you only pay for a membership when you're ready to start meeting your matches.",
    ],
    cta: { label: "Take the Survey", href: "https://survey.haevn.co" },
  },
];

export default function WhyNotSliderPage({
  surveyUrl = "https://survey.haevn.co",
  pagePath = "/whynot",
  trackingSource = "whynot_slider",
  slides: slidesData = SLIDES,
} = {}) {
  const [index, setIndex] = useState(0);
  // Real-time drag offset in pixels. Non-zero while the user's finger
  // is on the screen and dragging horizontally — the track follows the
  // finger for an Instagram/TikTok feel, then snaps to the next/prev
  // slide on release if the gesture cleared a threshold.
  const [touchDelta, setTouchDelta] = useState(0);
  // Video lightbox open state. When non-null, holds the slide's video
  // descriptor so the modal can render the right source.
  const [videoOpen, setVideoOpen] = useState(null);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const isDragging = useRef(false);

  // Forward the visitor's incoming query string (utm_*, gclid, fbclid,
  // ttclid, everything) onto every survey CTA. Hydration-safe: the
  // initial render uses the bare base URL (matching the prerendered
  // HTML), then this effect appends window.location.search verbatim.
  // Reading location.search synchronously here would re-trigger React
  // #418 and the white-flash bug — don't.
  const [querySuffix, setQuerySuffix] = useState("");
  useEffect(() => {
    setQuerySuffix((window.location.search || "").replace(/^\?/, ""));
  }, []);
  const effectiveSurveyUrl = querySuffix
    ? `${surveyUrl}${surveyUrl.includes("?") ? "&" : "?"}${querySuffix}`
    : surveyUrl;

  const slideCount = slidesData.length;

  const goTo = useCallback((i) => {
    const next = ((i % slideCount) + slideCount) % slideCount;
    setIndex(next);
  }, [slideCount]);

  const next = useCallback(() => setIndex((i) => (i + 1) % slideCount), [slideCount]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + slideCount) % slideCount),
    [slideCount],
  );

  // Keyboard navigation when the track is focused. Reader-paced — no
  // autoplay or play/pause, so ←/→ are the only bindings. ESC closes
  // the video lightbox when it's open.
  useEffect(() => {
    const onKey = (e) => {
      if (videoOpen && e.key === "Escape") {
        e.preventDefault();
        setVideoOpen(null);
        return;
      }
      if (videoOpen) return; // Lightbox open — don't navigate slides
      if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      }
    };
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("keydown", onKey);
    // Also listen on document so ESC works even when track isn't focused
    // (typical lightbox UX — users expect ESC anywhere).
    document.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onKey);
    };
  }, [next, prev, videoOpen]);

  // Lock body scroll while the video lightbox is open so the
  // background carousel can't be accidentally swiped underneath.
  useEffect(() => {
    if (!videoOpen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = orig;
    };
  }, [videoOpen]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (!isDragging.current && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      isDragging.current = true;
    }
    if (isDragging.current) {
      const atStart = index === 0 && dx > 0;
      const atEnd = index === slideCount - 1 && dx < 0;
      const damped = atStart || atEnd ? dx * 0.3 : dx;
      setTouchDelta(damped);
    }
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const width = trackRef.current ? trackRef.current.clientWidth : 0;
    const threshold = Math.min(80, Math.max(40, width * 0.18));
    setTouchDelta(0);
    if (isDragging.current && Math.abs(delta) > threshold) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
    isDragging.current = false;
  };

  const handleCtaClick = (label) => {
    trackSurveyStart({ source: trackingSource, placement: label });
  };

  // Per-variant CTA URL for the final slide. Rest of SLIDES is
  // unchanged — only the final slide has a `cta` object.
  const slides = slidesData.map((s) =>
    s.cta ? { ...s, cta: { ...s.cta, href: effectiveSurveyUrl } } : s,
  );

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white" data-testid="whynot-slider-page">
      <SEO
        path={pagePath}
        title="HAEVN | A New Way to Find Connection"
        description="A matching network built around shared intent. Take the 15-minute survey once. We do the searching. You only pay if you decide to connect with the matches we find."
        ogImage="https://haevn.co/og-image.jpg?v=2"
      />

      {/* Minimal landing-page header — logo only, transparent over the
          slide. No nav links. Goal: keep the viewer on the page. */}
      <header
        className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-6 py-5 md:px-10 md:py-6"
        data-testid="whynot-header"
      >
        <Link to="/" aria-label="HAEVN home" data-testid="whynot-logo">
          <HaevnLogo className="h-6 w-auto md:h-7" color="#FFFFFF" />
        </Link>
        {/* Mobile-only "back" affordance, shown on the closing card
            (slide 4) where the sticky bottom bar is taken over by
            the full-width CTA. Hidden on every other slide and on
            desktop (desktop keeps its prev/next chrome at the bottom). */}
        {index === slideCount - 1 && (
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white/85 backdrop-blur-sm transition-colors hover:border-white hover:text-white md:hidden"
            data-testid="whynot-mobile-back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </header>

      <main className="relative">
        {/* Full-bleed slide track */}
        <div
          ref={trackRef}
          tabIndex={-1}
          className="relative h-[100svh] min-h-[640px] w-full overflow-hidden outline-none touch-pan-x"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          data-testid="whynot-slider"
          aria-roledescription="carousel"
          aria-label="HAEVN — A new way to find connection"
        >
          {/* Horizontal track. Each slide is a 100%-wide flex item;
              translating the track by `-index * 100%` slides the next
              chapter into view. Touch swipe additionally adds a real-
              time drag offset (touchDelta) so the slide tracks the
              finger before snapping — the familiar Instagram /
              TikTok carousel feel. */}
          <div
            className="flex h-full will-change-transform"
            style={{
              transform: `translate3d(calc(${-index * 100}% + ${touchDelta}px), 0, 0)`,
              transition: touchDelta === 0
                ? "transform 520ms cubic-bezier(0.32, 0.72, 0, 1)"
                : "none",
            }}
            data-testid="whynot-track"
          >
          {slides.map((slide, i) => {
            const isActive = i === index;
            return (
              <article
                key={slide.id}
                className={`relative h-full w-full shrink-0 overflow-hidden ${
                  slide.noImage ? "bg-[#070A14]" : ""
                }`}
                aria-hidden={!isActive}
                aria-roledescription="slide"
                aria-label={`${slide.id} of ${slideCount}: ${slide.title}`}
                data-testid={`whynot-slide-${slide.id}`}
              >
                {/* No-image closing card: render a very quiet ambient
                    teal + gold radial wash. Kept at low opacity so the
                    slide reads as a calm dark canvas with just a hint
                    of depth — color cues from the rest of the page
                    without the saturation. */}
                {slide.noImage && (
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 55% 45% at 18% 30%, rgba(11, 92, 95, 0.08) 0%, transparent 65%), radial-gradient(ellipse 45% 40% at 88% 85%, rgba(201, 162, 39, 0.04) 0%, transparent 65%)",
                    }}
                  />
                )}

                {!slide.noImage && (
                  <>
                    {/* Image. Per-slide `flip` mirrors horizontally so the
                    subject doesn't sit under the copy column. The
                    mental model is:
                      1. source image is cropped via object-position
                      2. THEN transform: scaleX(-1) mirrors the result.

                    For subject-led shots (slide.flip === true) we ALSO
                    apply a non-uniform scale on mobile (`mobileZoom`)
                    so the image becomes taller than the viewport.
                    Without the zoom, the square source aspect-fits the
                    portrait viewport with no vertical crop — meaning
                    `object-position` Y has no effect and there's no
                    way to raise the subject. The zoom plus a vertical
                    bias of ~75% lifts the subject into the upper half
                    of the screen and crops the empty sky/buildings
                    above his head. */}
                <img
                  src={slide.image}
                  alt=""
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={i === 0 ? "high" : "auto"}
                  // Intrinsic dimensions help the browser reserve memory
                  // up-front and skip decoding off-screen slides when
                  // under memory pressure (the issue that was leaving
                  // slides blank on older iOS / low-RAM Android devices).
                  width="1600"
                  height="1066"
                  className={`absolute inset-0 h-full w-full object-cover ${
                    slide.flip
                      ? "object-[22%_75%] md:object-center"
                      : "object-center"
                  }`}
                  style={{
                    transform: `${slide.flip ? "scaleX(-1) " : ""}${
                      isActive ? "scale(1.04)" : "scale(1)"
                    }`,
                    transition: "transform 8000ms ease-out",
                  }}
                />

                {/* Mobile vertical-crop control. When a slide defines
                    `mobileImageHeight` or is flipped, we emit a CSS
                    rule that resizes/repositions the <img> element on
                    portrait viewports so object-position has vertical
                    excess to crop into. Each slide can be tuned
                    independently — face closeups use 100% (no zoom),
                    full-body silhouettes use a negative top offset to
                    lift the subject, lower-framed subjects (slide 2)
                    use a height + larger negative top to raise them
                    above the gradient zone. */}
                <style>{`
                  @media (max-width: 767px) {
                    [data-testid="whynot-slide-${slide.id}"] > img {
                      ${
                        slide.mobileObjectPosition
                          ? `object-position: ${slide.mobileObjectPosition};`
                          : ""
                      }
                      ${
                        slide.mobileImageHeight
                          ? `height: ${slide.mobileImageHeight}; top: ${slide.mobileImageTop || "0"};`
                          : slide.flip
                            ? "height: 135%; top: -17.5%;"
                            : ""
                      }
                    }
                  }
                  ${
                    slide.desktopObjectPosition
                      ? `@media (min-width: 768px) {
                    [data-testid="whynot-slide-${slide.id}"] > img {
                      object-position: ${slide.desktopObjectPosition};
                    }
                  }`
                      : ""
                  }
                `}</style>

                {/* Tonal overlay. Either a left-to-right wash (cinematic
                    establishing shots) or a bottom-up wash (subject-led
                    shots where we only want to darken under the copy
                    and keep the upper image visible). Per-slide
                    `overlayDesktop` override lets specific slides
                    raise/strengthen the gradient when imagery is
                    competing with the eyebrow text. */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{
                    background:
                      slide.overlayDesktop || OVERLAYS[slide.overlay || "left"],
                  }}
                />
                {/* On mobile only, layer a LIGHT pure-black gradient on
                    the lower portion. The previous version was too
                    aggressive — it darkened the subject up to the
                    shoulder line. This pass keeps the very bottom solid
                    (so type stays legible) but drops back to near-zero
                    opacity by ~45% of viewport height, leaving the
                    upper half of the subject untouched. Per-slide
                    `overlayMobile` override lets individual slides
                    raise the gradient ceiling when needed. */}
                {slide.overlay === "bottom" && (
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 md:hidden"
                    style={{
                      background:
                        slide.overlayMobile ||
                        "linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.75) 15%, rgba(0, 0, 0, 0.35) 30%, rgba(0, 0, 0, 0.10) 45%, transparent 60%)",
                    }}
                  />
                )}
                {/* Secondary atmospheric bottom wash — only for the
                    cinematic "left" overlay style; the "bottom" style
                    already includes its own vertical fade. */}
                {(!slide.overlay || slide.overlay === "left") && (
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(8, 11, 24, 0.55) 0%, transparent 50%)",
                    }}
                  />
                )}
                  </>
                )}

                {/* Copy column — bottom-left. The intro slide gets
                    extra bottom padding + tighter max-width on mobile
                    to land more like a book cover. */}
                <div className="relative h-full w-full">
                  <div
                    className={`absolute inset-0 flex items-end px-6 md:px-16 lg:px-24 ${
                      slide.intro
                        ? "pb-20 md:pb-32 lg:pb-36"
                        : slide.cta
                          ? "pb-32 md:pb-32 lg:pb-36"
                          : slide.numberedList
                            ? "pb-20 md:pb-32 lg:pb-36"
                            : "pb-28 md:pb-32 lg:pb-36"
                    }`}
                  >
                    <div
                      className={slide.intro ? "max-w-xl md:max-w-2xl" : "max-w-2xl"}
                      style={{
                        opacity: isActive ? 1 : 0,
                        transform: isActive ? "translateY(0)" : "translateY(16px)",
                        transition:
                          "opacity 900ms ease-out 200ms, transform 900ms ease-out 200ms",
                      }}
                    >
                      {/* Eyebrow — gold uppercase tagline. Optional;
                          the closing card omits it so the headline
                          carries the moment. */}
                      {slide.eyebrow && (
                        <p
                          className="text-[#E6C260]"
                          style={{
                            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                          }}
                        >
                          {slide.eyebrow}
                        </p>
                      )}

                      {/* Chapter counter — only shown on chapter slides
                          (not the intro or the closing card). Counts
                          01..N of the chapters. Sized large enough to
                          register against the imagery without
                          competing with the headline. */}
                      {!slide.intro && !slide.noImage && !slide.hideCounter && (
                        <div className="mt-5 flex items-baseline gap-2 text-white/85">
                          <span
                            style={{
                              fontFamily:
                                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                              fontWeight: 500,
                              fontSize: "clamp(2rem, 2.6vw, 2.6rem)",
                              lineHeight: 1,
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {String(slide.id).padStart(2, "0")}
                          </span>
                          <span
                            className="text-white/55"
                            style={{
                              fontFamily:
                                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                              fontWeight: 400,
                              fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                              letterSpacing: "0.02em",
                            }}
                          >
                            / {String(
                              slidesData.filter((s) => !s.intro).length,
                            ).padStart(2, "0")}
                          </span>
                        </div>
                      )}

                      {slide.title && (
                        <h2
                          className={`text-white ${
                            slide.intro
                              ? "mt-6 md:mt-7"
                              : slide.eyebrow
                                ? "mt-4"
                                : ""
                          }`}
                          style={{
                            fontFamily:
                              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                            fontWeight: 500,
                            // Intro headline is larger — it carries the whole
                            // hook on its own without competing chapter weight.
                            // Closing card (numberedList) gets a smaller cap so
                            // the title + list + body + CTA stack lands at the
                            // bottom of the desktop viewport instead of
                            // overflowing into the top half.
                            fontSize: slide.intro
                              ? "clamp(2.4rem, 5.5vw, 4.5rem)"
                              : slide.numberedList
                                ? "clamp(1.85rem, 3.4vw, 2.85rem)"
                                : "clamp(2rem, 4.6vw, 3.75rem)",
                            lineHeight: 1.08,
                            letterSpacing: "-0.018em",
                          }}
                        >
                          {slide.title}
                        </h2>
                      )}

                      {/* Optional editorial numbered list. Used by the
                          closing card to lay out the "things to
                          remember" beats — each item gets a small
                          gold serif numeral instead of a checkbox or
                          bullet, matching the rest of the page's
                          editorial vocabulary. Extra top margin when
                          no title precedes it so the eyebrow has
                          proper editorial breathing room. */}
                      {slide.numberedList && (
                        <ol
                          className={`space-y-2 md:space-y-2.5 ${
                            slide.title ? "mt-5 md:mt-6" : "mt-8 md:mt-10"
                          }`}
                        >
                          {slide.numberedList.map((item, idx) => (
                            <li
                              key={idx}
                              className="flex items-baseline gap-4"
                            >
                              <span
                                className="shrink-0 text-[#E6C260]"
                                style={{
                                  fontFamily:
                                    "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                                  fontWeight: 500,
                                  fontStyle: "italic",
                                  fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
                                  lineHeight: 1.2,
                                  width: "1.5em",
                                }}
                              >
                                {String(idx + 1).padStart(2, "0")}
                              </span>
                              <span
                                className="text-white/90"
                                style={{
                                  fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                                  fontWeight: 300,
                                  fontSize: "clamp(0.95rem, 1.05vw, 1.05rem)",
                                  lineHeight: 1.5,
                                }}
                              >
                                {item}
                              </span>
                            </li>
                          ))}
                        </ol>
                      )}

                      {slide.body && (
                        <div
                          className={`${
                            slide.numberedList
                              ? "mt-5 space-y-2 text-white/85 md:mt-6 md:space-y-2"
                              : "space-y-5 text-white/85"
                          } ${
                            slide.intro
                              ? "mt-8 md:mt-9"
                              : !slide.numberedList && "mt-7"
                          }`}
                          style={{
                            // Always Inter on the body copy — the
                            // Geist Mono accent carries the
                            // headlines and numbered list numerals,
                            // but body sentences read cleaner in a
                            // sans, especially on the closing card
                            // where the conversational beats
                            // ("You've got better things to do…
                            // That's kind of the point.") were
                            // feeling overly ornate in serif.
                            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                            fontWeight: 300,
                            fontStyle: "normal",
                            fontSize: slide.numberedList
                              ? "clamp(1rem, 1.15vw, 1.1rem)"
                              : slide.intro
                                ? "clamp(1.05rem, 1.25vw, 1.2rem)"
                                : "clamp(1rem, 1.18vw, 1.15rem)",
                            lineHeight: 1.6,
                            letterSpacing: "normal",
                          }}
                        >
                          {slide.body.map((p, idx) => (
                            <p key={idx}>{p}</p>
                          ))}
                        </div>
                      )}

                      {/* The Continue affordance previously lived here for
                          the intro slide. It's been removed at the
                          user's request — the standard slider chrome
                          (play / prev / next + progress bars) carries
                          navigation on the intro just like every other
                          slide. */}

                      {/* Optional overview-video link. Renders as a tiny
                          inline text link below the body — no thumbnail,
                          no length call-out — so it sits inside the
                          editorial composition without disrupting the
                          cinematic photography. Tap opens the lightbox. */}
                      {slide.video && (
                        <button
                          type="button"
                          onClick={() => {
                            setVideoOpen(slide.video);
                            track("whynot_video_open", { slide: slide.id });
                          }}
                          className="group mt-5 inline-flex items-center gap-2 text-[#E6C260] hover:text-white transition-colors md:mt-6"
                          style={{
                            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                            fontWeight: 500,
                            fontSize: "0.78rem",
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                          }}
                          data-testid={`whynot-video-link-${slide.id}`}
                        >
                          <span
                            aria-hidden="true"
                            className="grid h-7 w-7 place-items-center rounded-full border border-[#E6C260]/70 transition-colors group-hover:border-white"
                          >
                            <svg viewBox="0 0 16 16" className="ml-[1px] h-3 w-3 fill-current">
                              <path d="M4.5 2.5v11l9-5.5z" />
                            </svg>
                          </span>
                          <span className="border-b border-current pb-[2px]">
                            {slide.video.label}
                          </span>
                        </button>
                      )}

                      {slide.cta && (
                        <a
                          href={slide.cta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleCtaClick(slide.cta.label)}
                          className="mt-6 hidden items-center gap-2 border-2 border-[#E6C260] bg-transparent px-7 py-3 text-[#E6C260] transition-all duration-200 hover:bg-[#E6C260] hover:text-[#0A0E1A] md:mt-6 md:inline-flex"
                          style={{
                            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            borderRadius: 2,
                          }}
                          data-testid={`whynot-cta-${slide.id}`}
                        >
                          {slide.cta.label}
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
          </div>

          {/* Bottom controls bar — prev/next + slide indicators.
              Left-justified to align with the content column padding
              (px-6 md:px-16 lg:px-24). No autoplay, so no play/pause
              affordance — the reader sets the pace. */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 hidden items-center justify-between gap-4 px-6 pb-7 md:flex md:px-16 md:pb-9 lg:px-24"
            data-testid="whynot-controls"
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={prev}
                aria-label="Previous slide"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white/85 backdrop-blur-sm transition-colors hover:border-white hover:text-white"
                data-testid="whynot-prev"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next slide"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white/85 backdrop-blur-sm transition-colors hover:border-white hover:text-white"
                data-testid="whynot-next"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {/* Slide indicators — thin progress bars. Past slides
                filled; current slide active in gold; future slides
                empty. Static (no autoplay animation). */}
            <div className="flex items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2 md:gap-3">
                {slidesData.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to slide ${s.id}`}
                    aria-current={i === index ? "true" : undefined}
                    className="group relative h-[2px] w-10 overflow-hidden bg-white/25 transition-all md:w-14"
                    data-testid={`whynot-dot-${s.id}`}
                  >
                    <span
                      className="absolute inset-y-0 left-0 block"
                      style={{
                        width: i <= index ? "100%" : "0%",
                        backgroundColor: i === index ? "#E6C260" : "#fff",
                      }}
                    />
                  </button>
                ))}
              </div>

              {/* "Earned" CTA — chapter slides 01–03 only. The intro
                  card omits it so the page can land its first
                  impression without asking. The closing card already
                  has the big inline CTA in the copy column, so we
                  also omit it there to avoid a duplicate. */}
              {!slidesData[index].intro && !slidesData[index].cta && (
                <a
                  href={effectiveSurveyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackSurveyStart({
                      source: `${trackingSource}_sticky`,
                      placement: `desktop_bar_slide_${slidesData[index].id}`,
                    })
                  }
                  className="inline-flex items-center gap-1.5 border border-[#E6C260] bg-[#E6C260]/10 px-4 py-2 text-[#E6C260] transition-all duration-200 hover:bg-[#E6C260] hover:text-[#0A0E1A]"
                  style={{
                    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    borderRadius: 2,
                  }}
                  data-testid="whynot-desktop-earned-cta"
                >
                  Take the Survey
                  <ChevronRight className="h-3 w-3" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>

          {/* Mobile-only sticky bottom bar.
              - Title card (intro): nav only — no CTA so the page can
                introduce itself before asking.
              - Chapter slides (01–04): nav cluster on the left + a
                compact "earned" CTA on the right. By this point the
                reader has committed to the journey and the survey
                link should always be a tap away.
              - Closing card (has `cta`): bar morphs into the full-
                width hero CTA so the primary action dominates the
                final beat.
              A soft dark gradient sits behind the bar so the controls
              never wash out against a bright slide.
              Touch swipe + the back chevron in the top-right of the
              header still cover navigation; this bar is just the
              persistent thumb-zone chrome. */}
          <div
            className="absolute bottom-0 left-0 right-0 z-10 md:hidden pointer-events-none"
            data-testid="whynot-mobile-bar"
          >
            {/* Always-on backdrop — independent of the per-slide
                gradient overlays so the chrome reads on every slide. */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 h-32"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.15) 80%, transparent 100%)",
              }}
            />
            {slidesData[index].cta ? (
              // Closing card — full-width hero CTA only
              <div className="relative flex items-center px-6 pb-7 pt-6 pointer-events-auto">
                <a
                  href={slides[index].cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleCtaClick(slidesData[index].cta.label)}
                  className="flex w-full items-center justify-center gap-2 border-2 border-[#E6C260] bg-transparent px-7 py-3 text-[#E6C260] transition-all duration-200 hover:bg-[#E6C260] hover:text-[#0A0E1A]"
                  style={{
                    fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    borderRadius: 2,
                  }}
                  data-testid="whynot-mobile-cta"
                >
                  {slidesData[index].cta.label}
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            ) : (
              // Intro or chapter slide — nav cluster + (optionally) earned CTA
              <div className="relative flex items-center justify-between gap-3 px-6 pb-7 pt-6 pointer-events-auto">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous slide"
                    className="grid h-10 w-10 place-items-center rounded-full border border-white/50 bg-black/25 text-white/95 backdrop-blur-sm transition-colors hover:border-white hover:text-white"
                    data-testid="whynot-mobile-prev"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next slide"
                    className="grid h-10 w-10 place-items-center rounded-full border border-white/50 bg-black/25 text-white/95 backdrop-blur-sm transition-colors hover:border-white hover:text-white"
                    data-testid="whynot-mobile-next"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {/* "Earned" CTA — appears on chapter slides only.
                    The intro/hook slide intentionally omits it so the
                    page can land its first impression without asking
                    for anything. */}
                {!slidesData[index].intro && (
                  <a
                    href={effectiveSurveyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackSurveyStart({
                        source: `${trackingSource}_sticky`,
                        placement: `mobile_bar_slide_${slidesData[index].id}`,
                      })
                    }
                    className="inline-flex items-center gap-1 border border-[#E6C260] bg-[#E6C260]/10 px-4 py-2 text-[#E6C260] transition-all duration-200 hover:bg-[#E6C260] hover:text-[#0A0E1A]"
                    style={{
                      fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      borderRadius: 2,
                    }}
                    data-testid="whynot-mobile-earned-cta"
                  >
                    Take the Survey
                    <ChevronRight className="h-3 w-3" aria-hidden="true" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Scoped progress-bar animation. Re-keyed by `index` via the
            `key` prop on the bar element below so it restarts cleanly. */}
        <style>{`
          @keyframes whynot-progress {
            from { width: 0%; }
            to   { width: 100%; }
          }
          @keyframes whynot-continue-pulse {
            0%, 100% { opacity: 0.7; }
            50%      { opacity: 1; }
          }
          @keyframes whynot-continue-arrow {
            0%, 100% { transform: translateX(0); }
            50%      { transform: translateX(4px); }
          }
          @media (prefers-reduced-motion: reduce) {
            [data-testid="whynot-continue"],
            [data-testid="whynot-continue"] svg {
              animation: none !important;
            }
          }
        `}</style>
      </main>

      {/* Video lightbox overlay. Renders only when slide.video has been
          clicked. Vertical-friendly: the player keeps the video's
          aspect ratio so a portrait clip doesn't get stretched. Tap
          backdrop or close button (or ESC on desktop) to dismiss. */}
      {videoOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="HAEVN overview video"
          onClick={(e) => {
            // Close when clicking the backdrop (not the player itself)
            if (e.target === e.currentTarget) {
              setVideoOpen(null);
              track("whynot_video_close", { reason: "backdrop" });
            }
          }}
          data-testid="whynot-video-lightbox"
        >
          {/* Close button — top-right, far from the player so users
              don't accidentally tap it while interacting with controls. */}
          <button
            type="button"
            onClick={() => {
              setVideoOpen(null);
              track("whynot_video_close", { reason: "button" });
            }}
            aria-label="Close video"
            className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full border border-white/30 bg-black/40 text-white backdrop-blur-md transition-colors hover:border-white hover:bg-black/60 md:right-6 md:top-6"
            data-testid="whynot-video-close"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>

          {/* Player — capped height so a portrait video doesn't dwarf
              a desktop browser. max-w + max-h work together to keep
              vertical clips bounded on every screen size. */}
          <video
            key={videoOpen.src}
            src={videoOpen.src}
            controls
            autoPlay
            playsInline
            preload="auto"
            className="max-h-[90vh] max-w-[92vw] rounded-md shadow-2xl md:max-w-[420px]"
            onPlay={() => track("whynot_video_play", {})}
            onEnded={() => track("whynot_video_complete", {})}
            data-testid="whynot-video-player"
          >
            Sorry, your browser doesn&apos;t support embedded videos.
          </video>
        </div>
      )}

      {/* Minimal legal footer — bare necessities only. No nav links,
          no social, no "How it works" — keep the viewer on the page. */}
      <footer
        className="bg-[#0A0E1A] px-6 py-5 text-[11px] text-white/35 md:px-10"
        data-testid="whynot-footer"
        style={{ fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif", letterSpacing: "0.04em" }}
      >
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 md:flex-row">
          <p>© 2026 HAEVN. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              className="transition-colors hover:text-white/70"
              data-testid="whynot-footer-privacy"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="transition-colors hover:text-white/70"
              data-testid="whynot-footer-terms"
            >
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
